import React, { useState } from "react";
import { useAnketaI18n } from "src/internationalization";
import { CountUp } from "./motion";

/*
 * "From log to decision" - the event aggregation funnel.
 *
 * Mirrors the Events page aggregation control (OpsDedupe in the dashboards
 * plugin): Keine / Strikt / Regel / Gruppen, the "Geräte" toggle (collapse
 * across devices) and the MITRE filter (keep only ATT&CK-mapped events). The
 * selected step shows how many entries remain. All numbers are example values
 * and labelled as such. Themed via --ak-* tokens.
 */

const tp = "anketa.autonubil.home.funnel";

interface Mode {
  key: string;
  label: string;
  entries: number;
  descFb: string;
}

const MODES: Mode[] = [
  { key: "none", label: "Keine", entries: 2100000, descFb: "Every event on its own - 2,100,000 rows." },
  { key: "strict", label: "Strikt", entries: 1264000, descFb: "Identical log lines are bundled into one row." },
  { key: "rule", label: "Regel", entries: 18420, descFb: "One entry per rule and device - the rest as sub-events." },
  { key: "groups", label: "Gruppen", entries: 3180, descFb: "One entry per rule group and device." },
  { key: "devices", label: "Geräte", entries: 1410, descFb: "Across devices: the same rule group on every device = one entry." },
  { key: "mitre", label: "MITRE", entries: 212, descFb: "MITRE filter: only entries with an ATT&CK mapping." },
];

const fmt = (n: number) => n.toLocaleString("de-DE");

export const AutonubilAlertFunnel: React.FC = () => {
  const { translate } = useAnketaI18n();
  const [sel, setSel] = useState(MODES.length - 1);

  const steps = [
    { n: "2.100.000", label: translate(`${tp}.step1.label`, { defaultMessage: "Collect" } as any), desc: translate(`${tp}.step1.desc`, { defaultMessage: "events per day" } as any) },
    { n: "212", label: translate(`${tp}.step2.label`, { defaultMessage: "Aggregate" } as any), desc: translate(`${tp}.step2.desc`, { defaultMessage: "condensed entries" } as any) },
    { n: "69", label: translate(`${tp}.step3.label`, { defaultMessage: "Correlate" } as any), desc: translate(`${tp}.step3.desc`, { defaultMessage: "relevant alerts" } as any) },
    { n: "4", label: translate(`${tp}.step4.label`, { defaultMessage: "Act" } as any), desc: translate(`${tp}.step4.desc`, { defaultMessage: "critical - act now" } as any), hot: true },
  ];

  // Toggles stay "on" once the walk through the modes has passed them.
  const active = (i: number) => (i <= 3 ? i === Math.min(sel, 3) : i <= sel);

  return (
    <section id="aggregation" style={{ background: "var(--ak-page-bg)", padding: "clamp(64px, 9vw, 100px) clamp(24px, 5vw, 48px)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ maxWidth: 760, marginBottom: 40 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ak-accent)" }}>
            {translate(`${tp}.eyebrow`, { defaultMessage: "From log to decision" } as any)}
          </p>
          <h2 style={{ margin: "12px 0 0", fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.1, color: "var(--ak-text)" }}>
            {translate(`${tp}.title.pre`, { defaultMessage: "Millions of log events" } as any)}{" "}
            <span style={{ background: "linear-gradient(120deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", paddingBottom: "0.1em" }}>
              {translate(`${tp}.title.highlight`, { defaultMessage: "become a few decisions." } as any)}
            </span>
          </h2>
          <p style={{ margin: "16px 0 0", fontSize: "1.05rem", lineHeight: 1.65, color: "var(--ak-text-secondary)" }}>
            {translate(`${tp}.subtitle`, {
              defaultMessage:
                "opsanio bundles identical events, groups them by rule, rule group and device and filters on ATT&CK mappings - so your team only looks at what matters.",
            } as any)}
          </p>
        </div>

        {/* funnel */}
        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 230px), 1fr))", gap: 16 }}>
          {steps.map((s, i) => (
            <li
              key={s.label}
              style={{
                background: "var(--ak-surface)",
                border: `1px solid ${s.hot ? "var(--ak-sev-warning, #ffb454)" : i === 1 ? "var(--ak-accent-border)" : "var(--ak-border)"}`,
                borderRadius: 14,
                padding: "20px 22px 22px",
                boxShadow: "0 1px 4px var(--ak-shadow)",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ak-accent)" }}>
                {String(i + 1).padStart(2, "0")} · {s.label}
              </div>
              <div style={{ marginTop: 10, fontSize: "clamp(1.9rem, 3vw, 2.4rem)", fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", color: s.hot ? "var(--ak-sev-warning, #ffb454)" : "var(--ak-text)" }}>
                <CountUp value={s.n} />
              </div>
              <div style={{ marginTop: 4, fontSize: "0.95rem", color: "var(--ak-text-secondary)" }}>{s.desc}</div>
            </li>
          ))}
        </ol>
        <div aria-hidden="true" style={{ height: 4, borderRadius: 2, margin: "18px 0 0", background: "linear-gradient(90deg, var(--ak-accent) 0%, var(--ak-accent-2) 70%, var(--ak-sev-critical, #ff5d6c) 100%)" }} />

        {/* aggregation control, as on the dashboard's Events page */}
        <div style={{ marginTop: 24, background: "var(--ak-surface)", border: "1px solid var(--ak-accent-border)", borderRadius: 16, padding: "20px 22px 22px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ak-accent)" }}>
              {translate(`${tp}.panel.title`, { defaultMessage: "Aggregation · as in the opsanio dashboard" } as any)}
            </span>
            <span aria-live="polite" style={{ fontSize: "0.95rem", color: "var(--ak-text-secondary)", fontVariantNumeric: "tabular-nums" }}>
              {fmt(MODES[0].entries)} {translate(`${tp}.panel.events`, { defaultMessage: "events" } as any)} →{" "}
              <b style={{ color: "var(--ak-text)" }}>{fmt(MODES[sel].entries)}</b> {translate(`${tp}.panel.entries`, { defaultMessage: "entries" } as any)}
            </span>
          </div>
          <div role="group" aria-label={translate(`${tp}.panel.aria`, { defaultMessage: "Aggregation modes" } as any)} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
            {MODES.map((m, i) => {
              const on = active(i);
              return (
                <button
                  key={m.key}
                  type="button"
                  aria-pressed={i === sel}
                  onClick={() => setSel(i)}
                  style={{
                    cursor: "pointer",
                    padding: "9px 18px",
                    borderRadius: 10,
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    border: `1px solid ${on ? "var(--ak-accent)" : "var(--ak-border)"}`,
                    background: on ? "var(--ak-accent)" : "var(--ak-surface-2)",
                    color: on ? "var(--ak-text-on-accent)" : "var(--ak-text)",
                    marginLeft: i === 4 ? 10 : 0,
                    transition: "background 0.15s ease, color 0.15s ease",
                  }}
                >
                  {translate(`${tp}.mode.${m.key}`, { defaultMessage: m.label } as any)}
                </button>
              );
            })}
          </div>
          <p style={{ margin: "14px 0 0", fontSize: "0.97rem", lineHeight: 1.55, color: "var(--ak-text)" }}>
            {translate(`${tp}.mode.${MODES[sel].key}.desc`, { defaultMessage: MODES[sel].descFb } as any)}
          </p>
        </div>
        <p style={{ margin: "14px 0 0", fontSize: "0.8rem", letterSpacing: "0.04em", color: "var(--ak-text-muted)" }}>
          {translate(`${tp}.note`, { defaultMessage: "Example values for illustration." } as any)}
        </p>
      </div>
    </section>
  );
};

export default AutonubilAlertFunnel;
